import { useEffect, useState } from "react";
import { AlertCircle, Inbox, Loader2, Sparkles } from "lucide-react";

/**
 * Page layout primitives — width constraints, headers, states.
 *
 * <PageShell width="narrow|default|wide">
 *   <PageHeader eyebrow title subtitle action />
 *   <Section title subtitle>{children}</Section>
 * </PageShell>
 *
 * Default width = "default" (1100px). Use "narrow" (720px) for reading-heavy
 * surfaces (AI Expert centre column, Notes). Use "wide" (1320px) for tables
 * that genuinely need it (Reconciliation, Audit Findings).
 */

const WIDTH_CLS = {
    narrow: "max-w-[720px]",
    default: "max-w-[1100px]",
    wide: "max-w-[1320px]",
    full: "max-w-none",
};

export function PageShell({ width = "default", className = "", children, testId }) {
    const wcls = WIDTH_CLS[width] ?? WIDTH_CLS.default;
    return (
        <div
            className={`${wcls} mx-auto ${className}`.trim()}
            data-testid={testId}
        >
            {children}
        </div>
    );
}

export function PageHeader({ eyebrow, title, subtitle, action, className = "" }) {
    return (
        <header className={`mb-10 ${className}`.trim()}>
            <div className="flex items-start justify-between gap-6">
                <div className="flex-1 min-w-0">
                    {eyebrow && (
                        <div className="text-[12px] text-stone-500 font-medium mb-2">
                            {eyebrow}
                        </div>
                    )}
                    {title && (
                        <h1
                            className="text-display-lg"
                            data-testid="page-title"
                        >
                            {title}
                        </h1>
                    )}
                    {subtitle && (
                        <p className="text-body text-stone-500 mt-2 max-w-prose leading-relaxed">
                            {subtitle}
                        </p>
                    )}
                </div>
                {action && (
                    <div className="flex-shrink-0">{action}</div>
                )}
            </div>
        </header>
    );
}

export function Section({
    eyebrow,
    title,
    subtitle,
    action,
    className = "",
    children,
}) {
    return (
        <section className={`mb-12 ${className}`.trim()}>
            {(eyebrow || title || subtitle || action) && (
                <div className="flex items-end justify-between gap-6 mb-5">
                    <div className="flex-1 min-w-0">
                        {eyebrow && (
                            <div className="text-[12px] text-stone-500 font-medium mb-1.5">
                                {eyebrow}
                            </div>
                        )}
                        {title && (
                            <h2 className="text-display-md">{title}</h2>
                        )}
                        {subtitle && (
                            <p className="text-body-sm text-stone-500 mt-1.5 max-w-prose leading-relaxed">
                                {subtitle}
                            </p>
                        )}
                    </div>
                    {action && <div className="flex-shrink-0">{action}</div>}
                </div>
            )}
            {children}
        </section>
    );
}

export function Loading({ label = "Loading…", testId, cues = null, variant = "spinner" }) {
    // Variants:
    //   "spinner"   — small calm spinner + label (default)
    //   "skeleton"  — 3 muted lines pulsing (use on initial page loads)
    //   "thinking"  — sparkle + rotating cues (use while AI / heavy compute thinks)
    const [cueIndex, setCueIndex] = useState(0);
    useEffect(() => {
        if (variant !== "thinking" || !cues || cues.length <= 1) return;
        const t = setInterval(() => setCueIndex((i) => (i + 1) % cues.length), 1800);
        return () => clearInterval(t);
    }, [variant, cues]);

    if (variant === "skeleton") {
        return (
            <div className="space-y-3 py-2" data-testid={testId}>
                <div className="skeleton h-4 w-2/3" />
                <div className="skeleton h-4 w-5/6" />
                <div className="skeleton h-4 w-1/2" />
                <div className="sr-only">{label}</div>
            </div>
        );
    }

    if (variant === "thinking") {
        const display = cues && cues.length > 0 ? cues[cueIndex] : label;
        return (
            <div
                className="flex items-center gap-2.5 text-stone-600 text-[14px] py-4"
                data-testid={testId}
            >
                <Sparkles
                    size={14}
                    strokeWidth={2}
                    className="text-[var(--brand)] pulse-dot"
                />
                <span className="ds-fade-in" key={display}>{display}</span>
            </div>
        );
    }

    return (
        <div
            className="flex items-center gap-2 text-stone-500 text-sm py-6"
            data-testid={testId}
        >
            <Loader2 size={14} strokeWidth={2} className="animate-spin" />
            {label}
        </div>
    );
}

export function Empty({ title, hint, action, testId }) {
    return (
        <div
            className="text-center py-14"
            data-testid={testId}
        >
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-stone-100 mb-4">
                <Inbox size={18} strokeWidth={1.5} className="text-stone-500" />
            </div>
            <div className="text-display-sm">{title}</div>
            {hint && (
                <p className="text-stone-500 text-[14px] mt-2 max-w-md mx-auto leading-relaxed">
                    {hint}
                </p>
            )}
            {action && <div className="mt-5">{action}</div>}
        </div>
    );
}

export function ErrorState({ title = "Something went wrong", detail, retry, testId }) {
    return (
        <div
            className="rounded-lg border border-red-100 bg-red-50/50 px-5 py-4 flex items-start gap-3"
            data-testid={testId}
        >
            <AlertCircle size={16} strokeWidth={2} className="text-red-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-[14px] font-medium text-red-900">{title}</div>
                {detail && (
                    <div className="text-[13px] text-red-800/80 mt-1 leading-snug">
                        {detail}
                    </div>
                )}
                {retry && (
                    <button
                        type="button"
                        onClick={retry}
                        className="mt-2 text-[13px] text-red-900 underline underline-offset-2 hover:text-red-700"
                    >
                        Try again
                    </button>
                )}
            </div>
        </div>
    );
}
